/**
 * Lane A 1c (#1314) — translate the cloud runtime's `game.create@1`
 * streaming chunks into the `game:create:progress` push payload the SPA
 * "+ New Game" flow already consumes.
 *
 * `BrowserCrucibleClient.invoke('game:create', …)` routes the create
 * over `invokeStreaming` (the streaming chunks are
 * `game.create.chunk@1` notifications correlated by `params.requestId`),
 * then bridges each chunk through this translator into the existing
 * `game:create:progress` subscribers — so `NewGame.tsx` / `Dashboard`
 * render progress + the recoverable `created-not-catalogued` terminal
 * WITHOUT either call site changing.
 *
 * The runtime chunk shape mirrors `GameCreateOriginatorChunk`
 * (`packages/crucible/src/runtime/jsonrpc-methods.ts`): a `progress`
 * chunk (`phase` / `percent` / `message`) OR a `result` chunk (the
 * handler only forwards `ok:false` result chunks on the stream — the
 * `created-not-catalogued` orphan-guard terminal; the success result
 * settles the streaming `done`/result promise, NOT a chunk).
 *
 * The `CreateProgress` push payload shape comes from
 * `@volley/crucible-ipc-types` (`{ gameId, step, message, complete,
 * error?, warning?, recovery? }`). The renderer reads `message` (the
 * progress line), `error` (flips to the error stage), and `warning` /
 * `recovery` (the #441 non-fatal orphan-repo hint surface).
 */

import type { CreateProgress, CreateProgressStep } from '@volley/crucible-ipc-types';

/**
 * Mirror of the runtime's `GameCreateOriginatorChunk` (the
 * `game.create.chunk@1` wire shape). Re-declared here because
 * `crucible-ui` does not depend on `@volley/crucible`; the streaming
 * notifications arrive as `params` objects of this shape.
 */
export interface GameCreateChunk {
  chunkKind: 'progress' | 'result';
  phase?: string;
  percent?: number;
  message?: string;
  ok?: boolean;
  gameId?: string;
  slug?: string;
  repoUrl?: string;
  localPath?: string;
  error?: string;
  /** The handler's recoverable orphan-guard terminal (CRUCIBLE-453). */
  outcome?: 'created-not-catalogued';
}

/**
 * Map a runtime create-chunk's `phase` to the renderer's
 * `CreateProgressStep`. The renderer treats `step` as informational
 * (the visible signal is `message`), so an unrecognised phase falls
 * back to `'clone'` — the same neutral step the desktop handler uses
 * for narration / `info` lines.
 */
function phaseToStep(phase: string | undefined): CreateProgressStep {
  switch (phase) {
    case 'clone':
    case 'artifacts':
    case 'tokens':
    case 'generate':
    case 'git-init':
    case 'install':
    case 'build':
    case 'hero-generate':
    case 'git-commit':
    case 'github-repo':
    case 'git-push':
    case 'done':
    case 'failed':
      return phase;
    default:
      // 'scaffold' / 'registry' / 'ingot-overlay' / 'init-assets' and
      // any future phase: surface the message under a neutral step.
      return 'clone';
  }
}

/**
 * Translate ONE `game.create@1` streaming chunk into a `CreateProgress`
 * push payload, or `null` when the chunk carries nothing the renderer
 * should surface (defensive — e.g. a malformed empty chunk).
 *
 * - `progress` chunk → a `{ step, message, complete:false }` tick.
 * - `result` chunk with `ok:false` → the renderer's ERROR surface. The
 *   recoverable `created-not-catalogued` terminal is mapped to an
 *   `error` payload so the "+ New Game" flow surfaces the actionable
 *   "scaffolded but not catalogued" message rather than silently
 *   succeeding. (The success result is NOT a chunk — it settles the
 *   streaming result promise.)
 */
export function createGameChunkToProgress(chunk: GameCreateChunk): CreateProgress | null {
  // gameId is required on the CreateProgress shape; the SPA only reads
  // it for cache keys, so an empty string is acceptable mid-stream
  // (the success navigation uses the resolved CreateGameResult, not
  // these progress payloads).
  const gameId = typeof chunk.gameId === 'string' ? chunk.gameId : '';

  if (chunk.chunkKind === 'result') {
    if (chunk.ok === true) {
      // Success terminal is delivered via the streaming result promise,
      // not the progress channel — nothing to surface here.
      return null;
    }
    const errorMessage =
      chunk.error ??
      (chunk.outcome === 'created-not-catalogued'
        ? 'The game was created but could not be added to the catalogue. Retry from the catalogue-re-register path (do not re-create — the slug already exists).'
        : 'Game creation failed.');
    return {
      gameId,
      step: 'failed',
      message: errorMessage,
      complete: false,
      error: errorMessage,
    };
  }

  // progress chunk
  const message = typeof chunk.message === 'string' ? chunk.message : '';
  return {
    gameId,
    step: phaseToStep(chunk.phase),
    message,
    complete: false,
  };
}
